import React, { createContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { closeBudget, getUserData } from 'loot-core/client/actions';
import { setAuthState } from 'loot-core/client/actions/auth';
import {
  type AuthState,
  type BillingStatus,
} from 'loot-core/client/state-types/auth';
import { send } from 'loot-core/platform/client/fetch';
import * as asyncStorage from 'loot-core/platform/server/asyncStorage';

import { useDemoSite } from '../hooks/useDemoSite';
import { useLocalPref } from '../hooks/useLocalPref';
import { AsyncStorageKey, useStorage } from '../hooks/useStorage';

type User = {
  id?: string;
  email?: string;
  name?: string;
  country?: string;
  created_at?: string;
  offline: boolean;
};

type IAuthContext = {
  isAuthenticated: boolean;
  hasRemoteAccount: boolean;
  getUser: () => Promise<User>;
  reloadUser: () => Promise<User>;
  billingStatus: BillingStatus;
  refreshAuthState: () => Promise<AuthState>;
};
export const AuthContext = createContext<IAuthContext>(null);

export const AuthProvider = ({ children }) => {
  // @TODO: Refactor entire app to use Redux instead of relying on those states
  // Just to avoid 2 handlings
  const [budgetId] = useLocalPref('id');
  const dispatch = useDispatch();
  const { removeItem: removeUserLocalData } = useStorage(
    AsyncStorageKey.USER_DATA,
  );
  const { isDemoBudgetFile } = useDemoSite();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasRemoteAccount, setHasRemoteAccount] = useState(false);
  const [user, setUser] = useState(null);
  const [billingStatus, setBillingStatus] = useState<BillingStatus>({
    isActive: false,
  });

  useEffect(() => {
    if (hasRemoteAccount) {
      removeUserLocalData();
    }
  }, [hasRemoteAccount]);

  useEffect(() => {
    closeBudgetHandler();
  }, [budgetId, isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        hasRemoteAccount,
        getUser,
        reloadUser,
        billingStatus,
        refreshAuthState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );

  async function closeBudgetHandler() {
    if (budgetId && !isAuthenticated && !isDemoBudgetFile) {
      await dispatch(closeBudget());
    }
  }

  async function _isAuthenticated(): Promise<boolean> {
    const remoteAuth = await asyncStorage.getItem(AsyncStorageKey.USER_TOKEN);

    // this will only be set first time on login/signup
    if (!(await _hasRemoteAccount()) && !!remoteAuth) {
      asyncStorage.setItem(AsyncStorageKey.HAS_REMOTE_ACCOUNT, '1');
      setHasRemoteAccount(true);
    }

    return !!remoteAuth;
  }

  async function _hasRemoteAccount(): Promise<boolean> {
    const hasRemoteAccount = await asyncStorage.getItem(
      AsyncStorageKey.HAS_REMOTE_ACCOUNT,
    );
    return hasRemoteAccount === '1';
  }

  async function getUser() {
    if (!user) {
      return reloadUser();
    }
    return user;
  }

  async function reloadUser() {
    const profile = await send('subscribe-get-user');
    setUser(profile);
    return profile;
  }

  async function refreshAuthState() {
    const isAuthenticated = await _isAuthenticated();
    const hasRemoteAccount = await _hasRemoteAccount();

    let billingStatus: BillingStatus = { isActive: false };
    if (isAuthenticated) {
      billingStatus = await send('stripe-get-status');
      billingStatus = {
        ...billingStatus,
        canAddNewInstitution:
          billingStatus.reportUsageAbove > 0 ||
          billingStatus.maxInstitutions > billingStatus.currentUsage,
      };
    }

    setBillingStatus(billingStatus);

    let username: string;
    const userData = await dispatch(getUserData());
    username = userData.name || null;

    const state = {
      isAuthenticated,
      hasRemoteAccount,
      username,
      billingStatus,
    };

    // set in redux as well
    await dispatch(setAuthState(state));

    setHasRemoteAccount(hasRemoteAccount);
    setIsAuthenticated(isAuthenticated);

    return state;
  }
};
